import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import NewCustomerModal from '../customer-detail-view/components/modals/NewCustomerModal';

// ----------------------------------------------------------------------
// ✅ [타입 정의] 최신 API 명세 반영
// ----------------------------------------------------------------------

// 1. 고객 데이터 Interface
interface Customer {
  customerIdx: number;
  name: string;
  contact: string;
  manager: string;       // 담당자 이름
  contractDate: string;  // 계약일
  category: string;      // 상담/계약
  trousseau: string;     // 품목 (본식 등)
  rehearsalDate: string;
  weddingDate: string;
  status: string;        // 상담중/진행중/완료 등
  totalContractAmount?: number;
  paidCompletedAmount?: number;
  totalSupplyAmount?: number;
  profitAmount?: number;
  totalAmount: number;   // 총 계약금
  paidAmount: number;    // 정산금
  remainingAmount: number; // 잔금
  source: string;        // 유입경로
  orderCount: number;
  inventoryCount: number;
  reservationCount: number;
}

// 2. 상단 통계 Interface
interface DashboardStats {
  totalCount: number;
  totalContractAmount: number;
  totalPaidAmount: number;
  totalRemainingAmount?: number;
  totalSupplyAmount?: number;
  totalProfitAmount?: number;
  totalOrderCount: number;
}

// 3. 필터 옵션 Interface (API에서 받아올 구조)
interface FilterOptionStatus {
  label: string;
  value: string;
}

interface FilterOptionManager {
  idx: number | string; // 'ALL', 'UNASSIGNED', or UserIdx(number)
  name: string;
}

// 4. API 전체 응답 Interface
interface DashboardResponse {
  stats: DashboardStats;
  customers: Customer[];
  filterOptions: {
    status: FilterOptionStatus[];
    managers: FilterOptionManager[];
    category?: string[]; // 없을 수도 있으므로 optional
  };
}

// ----------------------------------------------------------------------
// ✅ [컴포넌트] 고객 목록 페이지
// ----------------------------------------------------------------------
const CustomerDetailPage = () => {
  const navigate = useNavigate();
  
  // --- State: 검색 및 필터 ---
  const [searchTerm, setSearchTerm] = useState('');
  
  // 필터 선택값 State
  const [statusFilter, setStatusFilter] = useState('ALL');       // 기본값 ALL
  const [managerFilter, setManagerFilter] = useState<string | number>('ALL'); // 기본값 ALL
  const [categoryFilter, setCategoryFilter] = useState('전체');

  // 필터 옵션 목록 State (API로 받아와서 채움)
  const [statusOptions, setStatusOptions] = useState<FilterOptionStatus[]>([
    { label: '전체', value: 'ALL' }
  ]);
  const [managerOptions, setManagerOptions] = useState<FilterOptionManager[]>([
    { idx: 'ALL', name: '전체' }
  ]);
  const [categoryOptions, setCategoryOptions] = useState<string[]>(['전체', '상담', '계약']);

  // --- State: 데이터 ---
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    totalCount: 0,
    totalContractAmount: 0,
    totalPaidAmount: 0,
    totalOrderCount: 0
  });
  const [isLoading, setIsLoading] = useState(false);

  // --- State: 모달 ---
  const [showAddModal, setShowAddModal] = useState(false);

  // ----------------------------------------------------------------------
  // ✅ [API] 대시보드 데이터 조회 (검색/필터 포함)
  // ----------------------------------------------------------------------
  const fetchDashboardData = useCallback(async () => {
    setIsLoading(true);
    try {
      // 쿼리 파라미터 구성
      const params = new URLSearchParams();
      if (searchTerm) params.append('searchTerm', searchTerm);
      
      params.append('statusFilter', statusFilter);
      params.append('managerFilter', String(managerFilter)); // ID를 문자열로 변환
      params.append('categoryFilter', categoryFilter);

      const token = localStorage.getItem('accessToken');
      
      const response = await fetch(`https://api.wedmin.biz/customer/dashboard?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('데이터를 불러오는데 실패했습니다.');
      }

      const result = await response.json();
      
      if (result.status && result.data) {
        const data = result.data as DashboardResponse;
        
        // 1. 목록 및 통계 업데이트
        setCustomers(data.customers);
        setStats(data.stats);

        // 2. 필터 옵션 업데이트 (API에서 내려온 값으로 교체)
        if (data.filterOptions) {
          if (Array.isArray(data.filterOptions.status)) {
            setStatusOptions(data.filterOptions.status);
          }
          if (Array.isArray(data.filterOptions.managers)) {
            setManagerOptions(data.filterOptions.managers);
          }
          // 카테고리는 API에 없으면 기존 로컬 값 유지, 있으면 업데이트
          if (Array.isArray(data.filterOptions.category) && data.filterOptions.category.length > 0) {
            setCategoryOptions(data.filterOptions.category);
          }
        }
      }
    } catch (error) {
      console.error('Dashboard Fetch Error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, statusFilter, managerFilter, categoryFilter]);

  // 필터나 검색어가 변경되면 데이터 다시 불러오기 (디바운싱 적용)
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchDashboardData();
    }, 300);

    return () => clearTimeout(timer);
  }, [fetchDashboardData]);

  // ----------------------------------------------------------------------
  // ✅ [핸들러] 이벤트 처리
  // ----------------------------------------------------------------------
  
  // 고객 상세 페이지 이동
  const handleCustomerClick = (customerId: number) => {
    navigate(`/dashboard/customer-detail-view?id=${customerId}`);
  };

  // 날짜 포맷팅
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // ----------------------------------------------------------------------
  // ✅ [렌더링] UI 구성
  // ----------------------------------------------------------------------
  return (
    <div className="flex h-screen bg-gray-50">
      {/* 사이드바 */}
      <Sidebar />
      
      <div className="flex-1 p-8 overflow-y-auto">
        {/* 1. 헤더 */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">고객관리</h1>
            <p className="text-sm text-gray-600 mt-1">고객별 발주, 입출고, 예약 정보를 통합 관리하세요</p>
          </div>
          <button 
            onClick={() => setShowAddModal(true)}
            className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap flex items-center gap-2 cursor-pointer"
          >
            <i className="ri-add-line"></i>
            고객 등록
          </button>
        </div>

        {/* 2. 통계 카드 (서버 데이터) */}
        <div className="grid grid-cols-4 gap-6 mb-6">
          <StatCard 
            title="총 고객수" 
            value={`${stats.totalCount}명`} 
            icon="ri-user-line" 
            color="blue" 
          />
          <StatCard 
            title="총 계약금액" 
            value={`${stats.totalContractAmount.toLocaleString()}원`} 
            icon="ri-money-dollar-circle-line" 
            color="green" 
          />
          <StatCard 
            title="총 정산금액" 
            value={`${stats.totalPaidAmount.toLocaleString()}원`} 
            icon="ri-wallet-line" 
            color="purple" 
          />
          <StatCard 
            title="총 발주건수" 
            value={`${stats.totalOrderCount}건`} 
            icon="ri-file-list-line" 
            color="orange" 
          />
        </div>

        {/* 3. 검색 및 필터 영역 */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            
            {/* 검색창 */}
            <div className="flex-1 min-w-64">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 flex items-center justify-center"></i>
                <input
                  type="text"
                  placeholder="고객명, 연락처, 담당자로 검색..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
            
            {/* 상태 필터 (API Data) */}
            <div className="flex gap-2 overflow-x-auto pb-1">
              {statusOptions.map((option) => (
                <FilterButton
                  key={option.value}
                  label={option.label}
                  isActive={statusFilter === option.value}
                  onClick={() => setStatusFilter(option.value)}
                  activeColor="blue"
                />
              ))}
            </div>
            
            {/* 담당자 필터 (API Data - IDX 기준) */}
            <div className="flex gap-2 overflow-x-auto pb-1">
              {managerOptions.map((manager) => (
                <FilterButton
                  key={manager.idx}
                  label={manager.name}
                  isActive={managerFilter === manager.idx}
                  onClick={() => setManagerFilter(manager.idx)}
                  activeColor="green"
                />
              ))}
            </div>
            
            {/* 카테고리 필터 */}
            <div className="flex gap-2 overflow-x-auto pb-1">
              {categoryOptions.map((category) => (
                <FilterButton
                  key={category}
                  label={category}
                  isActive={categoryFilter === category}
                  onClick={() => setCategoryFilter(category)}
                  activeColor="purple"
                />
              ))}
            </div>
          </div>
        </div>

        {/* 4. 고객 목록 리스트 */}
        {isLoading ? (
          <div className="flex justify-center p-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {customers.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <i className="ri-user-line text-4xl text-gray-300 mb-4"></i>
                <p className="text-gray-500">검색 조건에 맞는 고객이 없습니다.</p>
              </div>
            ) : (
              customers.map((customer) => (
                <div 
                  key={customer.customerIdx} 
                  className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow cursor-pointer border border-transparent hover:border-blue-200"
                  onClick={() => handleCustomerClick(customer.customerIdx)}
                >
                  {/* 카드 헤더 */}
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                        <i className="ri-user-line text-2xl text-blue-600"></i>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-xl font-bold text-gray-800">{customer.name}</h3>
                          <Badge status={customer.status} />
                          <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${
                            customer.category === '계약' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                          }`}>
                            {customer.category}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <i className="ri-phone-line w-4 h-4"></i>
                            <span>{customer.contact}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <i className="ri-user-line w-4 h-4"></i>
                            <span>담당: {customer.manager}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <i className="ri-calendar-line w-4 h-4"></i>
                            <span>계약일: {formatDate(customer.contractDate)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 금액 및 건수 그리드 */}
                  <div className="grid grid-cols-6 gap-4 mb-4">
                    <InfoBox label="총 계약금액" value={customer.totalContractAmount ?? customer.totalAmount} colorClass="text-gray-800" />
                    <InfoBox label="정산금액" value={customer.paidCompletedAmount ?? customer.paidAmount} colorClass="text-blue-600" />
                    <InfoBox label="잔여금액" value={customer.remainingAmount} colorClass="text-orange-600" />
                    <InfoBox label="발주건수" value={`${customer.orderCount}건`} colorClass="text-purple-600" />
                    <InfoBox label="입출고건수" value={`${customer.inventoryCount}건`} colorClass="text-green-600" />
                    <InfoBox label="예약건수" value={`${customer.reservationCount}건`} colorClass="text-indigo-600" />
                  </div>

                  {/* 카드 푸터 */}
                  <div className="flex items-center justify-between text-sm text-gray-600 border-t border-gray-100 pt-4">
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-1.5">
                        <i className="ri-calendar-event-line text-purple-500"></i>
                        <span>리허설: {formatDate(customer.rehearsalDate)}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <i className="ri-calendar-check-line text-pink-500"></i>
                        <span>본식: {formatDate(customer.weddingDate)}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <i className="ri-global-line text-gray-400"></i>
                        <span>유입: {customer.source}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-blue-600 font-medium hover:underline">상세보기 →</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* 5. 신규 고객 등록 모달 */}
      <NewCustomerModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          fetchDashboardData();
        }}
      />
    </div>
  );
};

// ----------------------------------------------------------------------
// ✅ [서브 컴포넌트] 가독성을 위해 분리
// ----------------------------------------------------------------------

// 상단 통계 카드
const StatCard = ({ title, value, icon, color }: { title: string, value: string, icon: string, color: string }) => (
  <div className="bg-white rounded-lg shadow-sm p-6">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-600 mb-1">{title}</p>
        <p className={`text-2xl font-bold ${color === 'gray' ? 'text-gray-800' : `text-${color}-600`}`}>{value}</p>
      </div>
      <div className={`w-12 h-12 bg-${color}-100 rounded-lg flex items-center justify-center`}>
        <i className={`${icon} text-xl text-${color}-600`}></i>
      </div>
    </div>
  </div>
);

// 필터 버튼
const FilterButton = ({ label, isActive, onClick, activeColor }: { label: string, isActive: boolean, onClick: () => void, activeColor: string }) => {
  const activeClass = `bg-${activeColor}-600 text-white`;
  const inactiveClass = 'bg-gray-100 text-gray-700 hover:bg-gray-200';
  
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${isActive ? activeClass : inactiveClass}`}
    >
      {label}
    </button>
  );
};

// 고객 상태 뱃지
const Badge = ({ status }: { status: string }) => {
  let style = 'bg-gray-100 text-gray-700';
  if (status === '진행중') style = 'bg-green-100 text-green-700';
  else if (status === '상담중') style = 'bg-blue-100 text-blue-700';
  else if (status === '완료') style = 'bg-purple-100 text-purple-700';
  else if (status === '취소') style = 'bg-red-100 text-red-700';

  return (
    <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${style}`}>
      {status}
    </span>
  );
};

// 그리드 정보 박스
const InfoBox = ({ label, value, colorClass }: { label: string, value: number | string, colorClass: string }) => (
  <div className="bg-gray-50 rounded-lg p-3 text-center sm:text-left">
    <div className="text-xs text-gray-500 mb-1">{label}</div>
    <div className={`text-lg font-bold ${colorClass}`}>
      {typeof value === 'number' ? value.toLocaleString() + '원' : value}
    </div>
  </div>
);

export default CustomerDetailPage;
